/**
 * vehicle-repayment.ts — how much of the vehicle price has been paid off, and what is left.
 *
 * THE DEPOSIT IS PART OF THE PRICE, AND THE VEHICLE TAB IS THE LAST SCREEN THAT SAID OTHERWISE.
 * The Summary tab has always counted the deposit among what the driver has paid, and the hire
 * purchase agreement's own Price is what the deposit and the weekly instalments together settle.
 * The Vehicle tab measured the weekly payments alone against the whole price, so a driver who put
 * money down read a balance they had already cleared — and once the engine starts crediting the
 * advances settled at the end of the contract, it stops at the price LESS that deposit, so the
 * leftover would have sat on this card as a permanent outstanding balance on a vehicle that is
 * paid for. Counting it here is what makes the two tabs agree. [D-B58]
 *
 * ── NET, BECAUSE A REFUND IS MONEY THE DRIVER GOT BACK ───────────────────────
 * `get_contract_insights` publishes the deposit both ways: `paid_total` is everything credited
 * and `amount` is that less anything refunded. Only the net figure has been kept against the
 * price, so only the net figure belongs in this arithmetic — a refunded deposit raises what is
 * still to pay by exactly what went back out, which is the same direction the engine moves.
 *
 * ── ONE VIEW ONLY, BY CONSTRUCTION ───────────────────────────────────────────
 * This takes no view and answers for the whole contract, where fleet-admin's twin
 * (`vehicleRepaid`) takes one and drops the deposit outside the "all" view — because no partner
 * has ever been paid a share of a contract deposit, so a share of one belongs in no card labelled
 * "Payout" or "Retained". The two apps agree today because the driver's Vehicle tab has no share
 * views to disagree in: its `activeView` is pinned to 'all' with no setter, and the card reads
 * these figures unscaled. WHOEVER GIVES THAT TAB A TOGGLE OWES THIS MODULE THE ADMIN'S RULE
 * FIRST — a view, and no deposit outside the "all" one.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT TOUCH ────────────────────────────────────
 * The weekly table and the Average Payment card count weekly payments, and a deposit is not a
 * weekly payment. They stay where they are, and on a contract whose advances are being recovered
 * the card and the table below it are two different measurements of two different things: the
 * card is the authoritative figure for what has gone to the vehicle.
 */

/** The contract deposit as `get_contract_insights` publishes it, to the depth this reads. */
export interface ContractDepositShape {
    /** NET — credits less refunds. `paid_total` is the gross figure and is not what counts here. */
    amount?: number | null;
    paid_total?: number | null;
    refunded_total?: number | null;
}

/** The two figures the Vehicle tab prints beside the price. */
export interface VehicleRepayment {
    /**
     * What has been paid towards the vehicle: the deposit the driver kept, plus the part of every
     * payment the engine booked to the contract. NOT capped at the price — a driver who has paid
     * past it is told so rather than shown a tidy number.
     */
    repaid: number;
    /** What is left of the price, floored at zero: a vehicle is paid for, never over-paid for. */
    outstanding: number;
}

/** A missing or unusable figure is nothing paid, which is the reading that cannot overstate. */
function amountOf(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Repaid and Outstanding against the vehicle price.
 *
 * `contractAllocation` is `allocation_summary.contract` — the vehicle's share of every payment,
 * as the engine split it. `soldAt` is the price; a contract with no price has nothing to owe.
 */
export function vehicleRepayment(
    soldAt: number | null | undefined,
    contractAllocation: number | null | undefined,
    deposit: ContractDepositShape | null | undefined,
): VehicleRepayment {
    const price = amountOf(soldAt);
    const repaid = amountOf(contractAllocation) + amountOf(deposit?.amount);

    return { repaid, outstanding: Math.max(0, price - repaid) };
}
